import type {TZDate} from '@date-fns/tz';
import {TZDateMini} from '@date-fns/tz';
import type {CalendarData, Locale} from '@sail/intl';
import type {DateGranularity} from '../date-range-picker/types';
import {
  getDateFromWeekNumber,
  getEndOfWeek,
  getFirstDayOfMonth,
  getFirstDayOfWeek,
  getFirstDayOfYear,
  getLastDayOfMonth,
  getLastDayOfYear,
  getMaxWeeksInYear,
  getWeekNumber,
} from '../dates';

/**
 * Converts a date to a string value in the format YYYY-MM-DD
 */
export function dateToDateFieldValue(date?: TZDate): string {
  if (!date) {
    return '--';
  }

  const year = date.getFullYear().toString().padStart(4, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return [year, month, day].join('-');
}

/**
 * Converts a date string in the format YYYY-MM-DD to an array of [year, month, day]
 */
export function dateFieldValueToParts(
  dateString: string,
): [number, number, number] {
  const [year, month, day] = dateString.split('-').filter(Boolean).map(Number);

  // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
  return [year, month, day];
}

/**
 * Converts a date string in the format YYYY-MM-DD to TZDate
 */
export function dateFieldValueToDate(
  dateString: string,
  timeZone: string,
): TZDate | undefined {
  if (dateString === '--' || dateString === '') {
    return undefined;
  }

  const [year, month, day] = dateFieldValueToParts(dateString);

  return new TZDateMini(year, month - 1, day, timeZone);
}

/**
 * Converts a date to a string value in the format YYYY-WW
 * Uses weekYear from getWeekNumber to handle year boundary cases correctly
 */
export function dateToWeekFieldValue(
  date: TZDate | undefined,
  locale: Locale,
  calendarData: CalendarData | undefined,
): string {
  if (!date) {
    return '-';
  }

  const {weekNumber, weekYear} = getWeekNumber(date, locale, calendarData);
  const parsedWeekNumber = weekNumber.toString().padStart(2, '0');

  return [weekYear, parsedWeekNumber].join('-');
}

/**
 * Converts a date string in the format YYYY-WW to an array of [year, week]
 */
export function weekFieldValueToParts(
  weekString: string,
): [number, number] | undefined {
  if (!weekString || weekString === '-') {
    return undefined;
  }

  const [yearStr, weekStr] = weekString.split('-');
  // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
  const year = parseInt(yearStr, 10);
  // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
  const week = parseInt(weekStr, 10);

  if (isNaN(year) || isNaN(week)) {
    return undefined;
  }

  return [year, week];
}

/**
 * Converts a date string in the format YYYY-WW to TZDate
 */
export function weekFieldValueToDate(
  weekString: string,
  timeZone: string,
  locale: Locale,
  calendarData: CalendarData | undefined,
): TZDate | undefined {
  const parts = weekFieldValueToParts(weekString);

  if (!parts) {
    return undefined;
  }

  const [year, week] = parts;

  return getDateFromWeekNumber(year, week, timeZone, locale, calendarData);
}

/**
 * Converts a date string in the format YYYY-WW to the end date of the week
 */
export function weekFieldValueToEndDate(
  weekString: string,
  timeZone: string,
  locale: Locale,
  calendarData: CalendarData | undefined,
): TZDate | undefined {
  const startDate = weekFieldValueToDate(
    weekString,
    timeZone,
    locale,
    calendarData,
  );

  if (!startDate) {
    return undefined;
  }

  const firstDayOfWeek = getFirstDayOfWeek(locale, calendarData);

  return getEndOfWeek(startDate, firstDayOfWeek);
}

/**
 * Converts a date to a string value in the format YYYY-MM
 */
export function dateToMonthFieldValue(date: TZDate | undefined): string {
  if (!date) {
    return '-';
  }

  const year = date.getFullYear().toString().padStart(4, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');

  return `${year}-${month}`;
}

/**
 * Converts a date string in the format YYYY-MM to an array of [year, month]
 */
export function monthFieldValueToParts(
  monthString: string,
): [number, number] | undefined {
  if (!monthString || monthString === '-') {
    return undefined;
  }

  const [yearStr, monthStr] = monthString.split('-');
  // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
  const year = parseInt(yearStr, 10);
  // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
  const month = parseInt(monthStr, 10);

  if (isNaN(year) || isNaN(month)) {
    return undefined;
  }

  return [year, month];
}

/**
 * Converts a date string in the format YYYY-MM to TZDate
 */
export function monthFieldValueToDate(
  monthString: string,
  timeZone: string,
): TZDate | undefined {
  const parts = monthFieldValueToParts(monthString);

  if (!parts) {
    return undefined;
  }

  const [year, month] = parts;

  return getFirstDayOfMonth(year, month - 1, timeZone);
}

/**
 * Converts a date string in the format YYYY-MM to the end date of the month
 */
export function monthFieldValueToEndDate(
  monthString: string,
  timeZone: string,
): TZDate | undefined {
  const parts = monthFieldValueToParts(monthString);

  if (!parts) {
    return undefined;
  }

  const [year, month] = parts;

  return getLastDayOfMonth(year, month - 1, timeZone);
}

/**
 * Converts a date to a string value in the format YYYY
 */
export function dateToYearFieldValue(date: TZDate | undefined): string {
  if (!date) {
    return '';
  }

  return date.getFullYear().toString().padStart(4, '0');
}

/**
 * Converts a date string in the format YYYY to TZDate
 */
export function yearFieldValueToDate(
  yearString: string,
  timeZone: string,
): TZDate | undefined {
  if (!yearString || yearString === '') {
    return undefined;
  }

  const year = parseInt(yearString, 10);

  if (isNaN(year)) {
    return undefined;
  }

  return getFirstDayOfYear(year, timeZone);
}

/**
 * Converts a date string in the format YYYY to the end date of the year
 */
export function yearFieldValueToEndDate(
  yearString: string,
  timeZone: string,
): TZDate | undefined {
  if (!yearString || yearString === '') {
    return undefined;
  }

  const year = parseInt(yearString, 10);
  if (isNaN(year)) {
    return undefined;
  }

  return getLastDayOfYear(year, timeZone);
}

/**
 * Converts a date to a granularity-aware string value
 * e.g. "2026-01-15"
 * - week: "2026-02"
 * - month: "2026-01"
 * - year: "2026"
 */
export function dateToGranularityFieldValue(
  date: TZDate | undefined,
  granularity: DateGranularity,
  locale: Locale,
  calendarData: CalendarData | undefined,
): string {
  switch (granularity) {
    case 'week':
      return dateToWeekFieldValue(date, locale, calendarData);
    case 'month':
      return dateToMonthFieldValue(date);
    case 'year':
      return dateToYearFieldValue(date);
    case 'day':
    default:
      return dateToDateFieldValue(date);
  }
}

/**
 * Given a granularity-aware string value, return the start date of the period
 * e.g. for week "2026-02" returns the first day of week 2, for month "2026-01" returns Jan 1
 */
export function granularityFieldValueToDate(
  value: string,
  granularity: DateGranularity,
  timeZone: string,
  locale: Locale,
  calendarData: CalendarData | undefined,
): TZDate | undefined {
  switch (granularity) {
    case 'week':
      return weekFieldValueToDate(value, timeZone, locale, calendarData);
    case 'month':
      return monthFieldValueToDate(value, timeZone);
    case 'year':
      return yearFieldValueToDate(value, timeZone);
    case 'day':
    default:
      return dateFieldValueToDate(value, timeZone);
  }
}

/**
 * Given a granularity-aware string value, return the end date of the period
 * e.g. for week "2026-02" returns the last day of week 2, for month "2026-01" returns Jan 31
 */
export function granularityFieldValueToEndDate(
  value: string,
  granularity: DateGranularity,
  timeZone: string,
  locale: Locale,
  calendarData: CalendarData | undefined,
): TZDate | undefined {
  switch (granularity) {
    case 'week':
      return weekFieldValueToEndDate(value, timeZone, locale, calendarData);
    case 'month':
      return monthFieldValueToEndDate(value, timeZone);
    case 'year':
      return yearFieldValueToEndDate(value, timeZone);
    case 'day':
    default:
      return dateFieldValueToDate(value, timeZone);
  }
}

/**
 * Validates a date field value (YYYY-MM-DD format)
 */
export function validateDateField(
  dateString: string,
  minDate: TZDate,
  maxDate: TZDate,
): boolean {
  if (!dateString || dateString === '--') {
    return true;
  }

  const [year, month, day] = dateFieldValueToParts(dateString);

  if (!year || !month || !day) {
    return false;
  }

  if (month <= 0 || month > 12) {
    return false;
  }

  const minYear = minDate.getFullYear();
  const maxYear = maxDate.getFullYear();
  const minMonth = minDate.getMonth() + 1;
  const maxMonth = maxDate.getMonth() + 1;

  if (year < minYear || year > maxYear) {
    return false;
  }

  if (
    (year === minYear && month < minMonth) ||
    (year === maxYear && month > maxMonth)
  ) {
    return false;
  }

  // month coming from DateField is 1-indexed while Date object is 0-indexed,
  // in here we're asking for day 0 of the next month, which corresponds to the last day of the current month
  const maxDaysInMonth = new Date(year, month, 0).getDate();

  if (day <= 0 || day > maxDaysInMonth) {
    return false;
  }

  const date = new TZDateMini(year, month - 1, day, minDate.timeZone);

  if (date < minDate || date > maxDate) {
    return false;
  }

  return true;
}

/**
 * Validates a week field value (YYYY-WW format)
 */
export function validateWeekYearField(
  weekString: string,
  minDate: TZDate,
  maxDate: TZDate,
  timeZone: string,
  locale: Locale,
  calendarData: CalendarData | undefined,
): boolean {
  if (!weekString || weekString === '-') {
    return true;
  }

  const parts = weekFieldValueToParts(weekString);

  if (!parts) {
    return false;
  }

  const [year, week] = parts;

  const minYear = minDate.getFullYear();
  const maxYear = maxDate.getFullYear();

  if (year < minYear || year > maxYear) {
    return false;
  }

  const maxWeeks = getMaxWeeksInYear(year, timeZone, locale, calendarData);

  if (week <= 0 || week > maxWeeks) {
    return false;
  }

  const firstDayOfWeek = getFirstDayOfWeek(locale, calendarData);

  const weekStartDate = getDateFromWeekNumber(
    year,
    week,
    timeZone,
    locale,
    calendarData,
  );

  const weekEndDate = getEndOfWeek(weekStartDate, firstDayOfWeek);

  // Check if the week has at least one enabled day within the [minDate, maxDate] range.
  // We check weekEndDate >= minDate (not weekStartDate >= minDate) because the week
  // may start before minDate but still have valid days. For example, if minDate is May 1st
  // and the week is April 30 - May 6, the week is valid because May 1-6 are enabled.
  if (weekEndDate < minDate || weekStartDate > maxDate) {
    return false;
  }

  return true;
}

/**
 * Validates a month field value (YYYY-MM format)
 */
export function validateMonthYearField(
  monthString: string,
  minDate: TZDate,
  maxDate: TZDate,
): boolean {
  if (!monthString || monthString === '-') {
    return true;
  }

  const parts = monthFieldValueToParts(monthString);

  if (!parts) {
    return false;
  }

  const [year, month] = parts;

  if (month <= 0 || month > 12) {
    return false;
  }

  const minYear = minDate.getFullYear();
  const maxYear = maxDate.getFullYear();
  const minMonth = minDate.getMonth() + 1;
  const maxMonth = maxDate.getMonth() + 1;

  if (year < minYear || year > maxYear) {
    return false;
  }

  if (
    (year === minYear && month < minMonth) ||
    (year === maxYear && month > maxMonth)
  ) {
    return false;
  }

  return true;
}

/**
 * Validates a year field value (YYYY format)
 */
export function validateYearField(
  yearString: string,
  minDate: TZDate,
  maxDate: TZDate,
): boolean {
  if (!yearString || yearString === '') {
    return true;
  }

  const year = parseInt(yearString, 10);

  if (isNaN(year)) {
    return false;
  }

  const minYear = minDate.getFullYear();
  const maxYear = maxDate.getFullYear();

  if (year < minYear || year > maxYear) {
    return false;
  }

  return true;
}

/**
 * Validates a granularity-aware field value
 */
export function validateGranularityField(
  value: string,
  granularity: DateGranularity,
  minDate: TZDate,
  maxDate: TZDate,
  timeZone: string,
  locale: Locale,
  calendarData: CalendarData | undefined,
): boolean {
  switch (granularity) {
    case 'week':
      return validateWeekYearField(
        value,
        minDate,
        maxDate,
        timeZone,
        locale,
        calendarData,
      );
    case 'month':
      return validateMonthYearField(value, minDate, maxDate);
    case 'year':
      return validateYearField(value, minDate, maxDate);
    case 'day':
    default:
      return validateDateField(value, minDate, maxDate);
  }
}
